import React, { useEffect, useState } from "react";
import { useDebounce } from "../../../hooks/debouncing";
import { DailogBox } from "../../../components/Elements/DailogBox/DailogBox";
import { Button, Input, Select } from "antd";
import CustomButton from "../../../components/Elements/Buttons/CustomButtons";
import { getApi, postApi } from "../../../api/api_instance";
import { API_URLS } from "../../../api/apiUrls";
// import toast from "react-hot-toast";

const AddNewProject = ({
  onOpen,
  onClose,
  onSave,
  onError,
  name,
  clientId,
  customer = false,
}) => {
  const [customerName, setCustomerName] = useState(name ? name : "");
  const [customerNameList, setCustomerNameList] = useState([]);
  const [projectType, setProjectType] = useState("");
  const [projectTypeOptions, setProjectTypeOptions] = useState([]);
  const [errorMessage, setErrorMessage] = useState({
    customerName: "",
    projectType: "",
  });

  const [customerSearch, setCustomerSearch] = useState("");
  const [projectTypeSearch, setProjectTypeSearch] = useState("");

  const debouncedCustomerSearch = useDebounce(customerSearch, 500);
  const debouncedProjectTypeSearch = useDebounce(projectTypeSearch, 500);

  const [customerPage, setCustomerPage] = useState(1);
  const [hasMoreCustomer, setHasMoreCustomer] = useState(true);
  const [loadingCustomer, setLoadingCustomer] = useState(false);

  const [projectTypePage, setProjectTypePage] = useState(1);
  const [hasMoreProjectType, setHasMoreProjectType] = useState(true);
  const [loadingProjectType, setLoadingProjectType] = useState(false);

  const fetchProjectTypes = async (page = 1, search = "", append = false) => {
    if (loadingProjectType) return;
    setLoadingProjectType(true);
    try {
      const response = await getApi({
        apiUrl: API_URLS.ALL_PROJECT_TYPES(page, 20, search),
      });
      if (response.success) {
        const activeItems = response.data.data.filter(
          (item) => item.status !== "Inactive"
        );
        const newData = activeItems.map((item) => ({
          label: item.project_type,
          value: item._id,
        }));
        setProjectTypeOptions((prev) =>
          append ? [...prev, ...newData] : newData
        );
        setHasMoreProjectType(response.data.data.length === 20);
      }
    } catch (error) {
      console.error("Error fetching project types:", error);
    } finally {
      setLoadingProjectType(false);
    }
  };

  const fetchCustomerNames = async (page = 1, search = "", append = false) => {
    if (loadingCustomer) return;
    setLoadingCustomer(true);
    try {
      const response = await getApi({
        apiUrl: API_URLS.GET_ALL_CUSTOMER(page, 20, "", search, ""),
      });
      if (response.success) {
        const data = Array.isArray(response.data)
          ? response.data
          : response.data.data || [];
        const customer = data.map((item) => ({
          label: item.organization?.name || item.name,
          value: item._id,
        }));
        setCustomerNameList((prev) =>
          append ? [...prev, ...customer] : customer
        );
        setHasMoreCustomer(customer.length === 20);
      }
    } catch (error) {
      console.error("Error fetching customer names:", error);
    } finally {
      setLoadingCustomer(false);
    }
  };

  const handleCustomerScroll = (e) => {
    const { target } = e;
    const isBottom =
      Math.ceil(target.scrollTop + target.offsetHeight) >=
      target.scrollHeight - 20;
    if (isBottom && hasMoreCustomer && !loadingCustomer) {
      setCustomerPage((prev) => {
        const nextPage = prev + 1;
        fetchCustomerNames(nextPage, debouncedCustomerSearch, true);
        return nextPage;
      });
    }
  };

  const handleProjectTypeScroll = (e) => {
    const { target } = e;
    const isBottom =
      Math.ceil(target.scrollTop + target.offsetHeight) >=
      target.scrollHeight - 20;
    if (isBottom && hasMoreProjectType && !loadingProjectType) {
      setProjectTypePage((prev) => {
        const nextPage = prev + 1;
        fetchProjectTypes(nextPage, debouncedProjectTypeSearch, true);
        return nextPage;
      });
    }
  };

  const createProject = async (data) => {
    try {
      const response = await postApi({
        apiUrl: customer
          ? API_URLS.CREATE_CUSTOMER_PROJECT
          : API_URLS.CREATE_PROJECT,
        data,
        headers: { client: clientId },
      });
      if (response?.success) {
        onSave();
      }
    } catch (error) {
      onError(error.message);
      onClose();
    } finally {
      onClose();
    }
  };

  const handleSave = async () => {
    let isValid = true;
    const errors = { customerName: "", projectType: "" };

    // Validate project type
    if (!projectType) {
      errors.projectType = "Project Type is required";
      isValid = false;
    }

    if (!customerName) {
      errors.customerName = "Customer Name is required";
      isValid = false;
    }

    setErrorMessage(errors);

    if (isValid) {
      const today = new Date();
      const formattedDate = `${String(today.getDate()).padStart(
        2,
        "0"
      )}/${String(today.getMonth() + 1).padStart(
        2,
        "0"
      )}/${today.getFullYear()}`;

      const data = {
        client_id: customerName,
        project_type: projectType,
        // status: "Project Created",
        standard_form: [],
        custom_form: [],
      };

      const customerData = {
        project_type: projectType,
        // status: "Project Created",
        standard_form: [],
        custom_form: [],
      };
      if (customer) {
        await createProject(customerData);
      } else {
        await createProject(data);
      }
    }
  };

  const handleCancel = () => {
    setCustomerName("");
    setProjectType("");

    setErrorMessage({ startDate: "", endDate: "", projectType: "" });
    onClose();
  };

  useEffect(() => {
    setProjectTypePage(1);
    setHasMoreProjectType(true);
    fetchProjectTypes(1, debouncedProjectTypeSearch, false);
  }, [debouncedProjectTypeSearch]);

  useEffect(() => {
    setCustomerPage(1);
    setHasMoreCustomer(true);
    fetchCustomerNames(1, debouncedCustomerSearch, false);
  }, [debouncedCustomerSearch]);

  return (
    <div>
      <DailogBox open={onOpen} onCancel={onClose} width="90%" maxWidth="700px">
        <div>
          <h3 className=" text-xl">Create Project</h3>
        </div>
        <div className="p-2 mt-6">
          <hr />
        </div>
        <div className="flex flex-col w-full mb-8">
          <Select
            value={name || customerName || null}
            onChange={setCustomerName}
            onSearch={setCustomerSearch}
            onPopupScroll={handleCustomerScroll}
            placeholder={"Select Customer Name"}
            className="text-lg font-light h-14"
            disabled={name}
            allowClear
            showSearch
            filterOption={false}
            loading={loadingCustomer}
          >
            {customerNameList.map((name) => (
              <Select.Option key={name.value} value={name.value}>
                {name.label}
              </Select.Option>
            ))}
          </Select>
        </div>
        {/* <div className="flex flex-col w-full">
          <Input
            type={isStartFocused ? "date" : "text"}
            value={startDate}
            onFocus={() => setIsStartFocused(true)}
            onBlur={(e) => {
              if (!e.target.value) setIsStartFocused(false);
            }}
            onChange={(e) => setStartDate(e.target.value)}
            placeholder="Start Date"
            style={{
              border: "1px solid #ebedf3",
              borderRadius: "10px",
              padding: "10px",
            }}
            className="text-base font-light h-14"
          />
          {errorMessage.startDate && (
            <div className="text-red-500 text-sm">{errorMessage.startDate}</div>
          )}
        </div> */}
        <div className="flex flex-col w-full mt-8">
          <Select
            value={projectType || null}
            onChange={setProjectType}
            onSearch={setProjectTypeSearch}
            onPopupScroll={handleProjectTypeScroll}
            placeholder="SELECT PROJECT SERVICES"
            className="text-lg font-light h-14"
            allowClear
            showSearch
            filterOption={false}
            loading={loadingProjectType}
          >
            {projectTypeOptions.map((type) => (
              <Select.Option key={type.value} value={type.label}>
                {type.label}
              </Select.Option>
            ))}
          </Select>
          {errorMessage.projectType && (
            <div className="text-red-500 text-sm">
              {errorMessage.projectType}
            </div>
          )}
        </div>
        <div className="flex justify-center gap-4 py-6">
          <CustomButton
            buttonType="primary"
            variant="cancel"
            buttonText="Cancel"
            className="text-base font-normal h-[50px] w-[200px]"
            onClick={handleCancel}
          />
          <CustomButton
            buttonType="primary"
            variant="ok"
            buttonText="Save"
            className="text-base font-normal h-[50px] w-[200px]"
            onClick={handleSave}
          />
        </div>
      </DailogBox>
    </div>
  );
};

export default AddNewProject;
